import {
  componentsReady,
  PButton,
  PButtonPure,
  PInputText,
} from '@porsche-design-system/components-react';
import { Column, RowData } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';

import { ActionGroup, Popover } from '../..';
import type { FilterTableFeatures } from '../table-features';

export const TextFilter = <
  TFeatures extends FilterTableFeatures,
  T extends RowData,
>({
  column,
  i18n,
}: {
  column: Column<TFeatures, T, unknown>;
  i18n: {
    actionSearch: string;
    buttonReset: string;
    buttonFilter: string;
    filterLabel: string;
  };
}) => {
  const [isVisible, setVisibility] = useState(false);
  const filterColumn = column as unknown as Column<
    FilterTableFeatures,
    T,
    unknown
  >;
  const filterValue = filterColumn.getFilterValue() as string;
  const [searchString, setSearchString] = useState(filterValue);
  const inputEl = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearchString(filterValue);
  }, [filterValue]);

  // Native autofocus does not work here
  useEffect(() => {
    const focus = async () => {
      await componentsReady();
      inputEl?.current?.focus();
    };
    focus();
    // oxlint-disable-next-line react/exhaustive-effect-dependencies
  }, [isVisible]);

  const applyFilter = () => {
    filterColumn.setFilterValue(searchString || undefined);
    setVisibility(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) =>
    event.key === 'Enter' && applyFilter();

  return (
    <Popover
      target={
        <PButtonPure
          type="button"
          active={!!filterValue}
          icon="search"
          size="xs"
          onClick={() => setVisibility((value) => !value)}
          hideLabel
        >
          {i18n.filterLabel}
        </PButtonPure>
      }
      open={isVisible}
      onChange={setVisibility}
      placement="right-start"
      withPortal
    >
      <>
        <PInputText
          name="text-filter"
          hideLabel
          label={i18n.actionSearch}
          aria-label={i18n.actionSearch}
          value={searchString || ''}
          ref={inputEl as React.Ref<HTMLInputElement>}
          onInput={(e) => {
            const { value } = e.target as HTMLInputElement;
            setSearchString(value);
          }}
          onKeyDown={handleKeyDown}
        />

        <ActionGroup spacing="md">
          <PButton
            type="button"
            variant="secondary"
            icon="close"
            onClick={() => {
              setSearchString('');
              filterColumn.setFilterValue(undefined);
              setVisibility(false);
            }}
          >
            {i18n.buttonReset}
          </PButton>
          <PButton type="button" onClick={applyFilter}>
            {i18n.buttonFilter}
          </PButton>
        </ActionGroup>
      </>
    </Popover>
  );
};
